import React, { useState, useEffect } from 'react';
import { Bell, Mail, MessageSquare, Smartphone, Send } from 'lucide-react';
import { useNotificationConfig, useUpdateNotificationConfig, useTestNotification } from '@/hooks/useApi';
import { Card, Badge, LoadingSpinner } from '@/components/UI';

export function NotificationSettings() {
  const { data: config, isLoading } = useNotificationConfig();
  const updateConfig = useUpdateNotificationConfig();
  const testNotif = useTestNotification();
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (config && !dirty) {
      setForm(config as Record<string, unknown>);
    }
  }, [config, dirty]);

  const handleSave = async () => {
    try {
      await updateConfig.mutateAsync(form);
      setDirty(false);
    } catch { /* handled */ }
  };

  const handleTest = async (channel: string) => {
    try {
      await testNotif.mutateAsync(channel);
    } catch { /* handled */ }
  };

  if (isLoading) return <LoadingSpinner text="Loading notification settings..." />;

  const channels = [
    { key: 'email', enabled: form.email_enabled, label: 'Email', icon: Mail, fields: [
      { key: 'email_address', label: 'Email Address', type: 'email' },
    ]},
    { key: 'discord', enabled: form.discord_enabled, label: 'Discord', icon: MessageSquare, fields: [
      { key: 'discord_webhook', label: 'Webhook URL', type: 'text' },
    ]},
    { key: 'telegram', enabled: form.telegram_enabled, label: 'Telegram', icon: Smartphone, fields: [
      { key: 'telegram_bot_token', label: 'Bot Token', type: 'text' },
      { key: 'telegram_chat_id', label: 'Chat ID', type: 'text' },
    ]},
    { key: 'browser', enabled: form.browser_enabled, label: 'Browser', icon: Bell, fields: [] },
  ];

  const categories = [
    { key: 'notify_on_trade', label: 'Trade Executed' },
    { key: 'notify_on_risk', label: 'Risk Alerts' },
    { key: 'notify_on_ai', label: 'AI Decisions' },
    { key: 'notify_on_system', label: 'System Events' },
  ];

  return (
    <div className="space-y-6">
      {/* Channels */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {channels.map(({ key, enabled, label, icon: Icon, fields }) => (
          <Card key={key}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-brand-400" />
                <span className="text-sm font-medium text-surface-200">{label}</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!enabled}
                  onChange={(e) => {
                    setForm({ ...form, [`${key}_enabled`]: e.target.checked });
                    setDirty(true);
                  }}
                  className="sr-only peer"
                />
                <div className="w-9 h-5 bg-surface-700 peer-focus:outline-none rounded-full peer peer-checked:bg-brand-600 transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full"></div>
              </label>
            </div>
            {fields.map((field) => (
              <div key={field.key} className="mt-2">
                <label className="block text-xs text-surface-500 mb-1">{field.label}</label>
                <input
                  type={field.type}
                  className="input"
                  value={String(form[field.key] ?? '')}
                  onChange={(e) => {
                    setForm({ ...form, [field.key]: e.target.value });
                    setDirty(true);
                  }}
                />
              </div>
            ))}
            {enabled ? (
              <button
                onClick={() => handleTest(key)}
                className="btn-ghost btn-sm mt-3"
                disabled={testNotif.isPending}
              >
                <Send className="w-3.5 h-3.5" /> Test
              </button>
            ) : null}
          </Card>
        ))}
      </div>

      {/* Notification Categories */}
      <Card title="Notification Categories">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {categories.map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={!!form[key]}
                onChange={(e) => {
                  setForm({ ...form, [key]: e.target.checked });
                  setDirty(true);
                }}
                className="rounded border-surface-600 bg-surface-800 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-sm text-surface-300">{label}</span>
            </label>
          ))}
        </div>
      </Card>

      {/* Save */}
      {dirty && (
        <div className="flex justify-end">
          <button onClick={handleSave} className="btn-primary" disabled={updateConfig.isPending}>
            {updateConfig.isPending ? 'Saving...' : 'Save Notification Settings'}
          </button>
        </div>
      )}
    </div>
  );
}
